/**
 * Error boundary para chunk-load-error tras deploys.
 *
 * Cuando una SPA está abierta y se hace un nuevo deploy a Cloudflare Pages,
 * los hashes de los chunks anteriores desaparecen. Al navegar a un tab nuevo
 * el browser intenta cargar el chunk viejo y falla con:
 *   "Failed to fetch dynamically imported module"  (Chrome/Safari)
 *   "ChunkLoadError"                               (Webpack compat)
 *
 * Este boundary detecta ese error y fuerza un window.location.reload() para
 * que el usuario cargue los assets nuevos automáticamente, sin mensaje de
 * error visible.
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Fallback a mostrar mientras se recarga (típicamente null — recarga es inmediata) */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("ChunkLoadError") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // Recarga automática — el usuario verá el nuevo deploy
      console.warn("[ChunkErrorBoundary] chunk-load-error detectado, recargando…", error, info);
      window.location.reload();
    } else {
      console.error("[ChunkErrorBoundary] error no-chunk:", error, info);
    }
  }

  override render(): ReactNode {
    const { hasError, isChunkError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (isChunkError) {
        // La recarga está en curso — mostrar nada (o el fallback)
        return fallback ?? null;
      }
      // Error real no relacionado con chunks
      return (
        <div className="error-banner" role="alert">
          <span className="error-banner-icon">⚠</span>
          <span className="error-banner-msg">Error al cargar este panel. Recargá la página.</span>
          <button
            type="button"
            className="error-banner-retry"
            onClick={() => window.location.reload()}
          >
            Recargar
          </button>
        </div>
      );
    }

    return children;
  }
}
